/**
 * OutcomeReasonModal
 *
 * Pergunta o MOTIVO quando um contato é movido para uma coluna que fecha o
 * funil (etapa marcada como ganho/perda em LabelConfigDrawer). Texto livre,
 * gravado em campo nativo do contato por FUNIL (outcome_reasons[pipeline_id]).
 *
 * O preenchimento NÃO é obrigatório: "Pular" move o contato do mesmo jeito —
 * mantém a filosofia do produto, onde campo obrigatório é sinalização e nunca
 * trava a operação. Por isso o commit da movimentação acontece no fechamento
 * do modal, com ou sem motivo.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Trophy, XCircle, X } from 'lucide-react';

// Marcas de acentuacao (combining diacriticals). Montado via RegExp a partir de
// string ASCII de proposito: escrever o intervalo cru no fonte deixa o arquivo
// refem da codificacao do editor.
const DIACRITICS = new RegExp('[\\u0300-\\u036f]', 'g');

const OutcomeReasonModal = ({
  isOpen, colors, outcome, labelName, contactName, onConfirm, onSkip,
  // Motivos sugeridos da etapa. VAZIO = nenhum atalho aparece e o modal fica
  // igual ao de antes (só texto livre) — é o que impede quebrar clientes que
  // nunca configuraram lista nenhuma.
  options = [],
  // Contas com CRM comercial (crm-config com value_field) pedem o VALOR DA
  // VENDA no ganho — é o ponto natural de captura do VGV. false = modal igual
  // ao de antes. O valor sai como 2º argumento de onConfirm/onSkip:
  // { saleValue: number|null } — e é salvo mesmo quando o motivo é pulado.
  askValue = false,
  // Data REAL da venda/perda (crm-config.outcome_date, pedido do cliente
  // myCar 20/10/2026): o time registra a venda dias depois e o painel contava
  // no dia do movimento. Default hoje; sai como extra.outcomeAt (AAAA-MM-DD).
  askDate = false,
}) => {
  const [reason, setReason] = useState('');
  const [saleValue, setSaleValue] = useState('');
  const [outcomeAt, setOutcomeAt] = useState('');
  const inputRef = useRef(null);
  const valueRef = useRef(null);

  const isWon = outcome === 'won';
  const showValue = isWon && askValue;
  const showDate = askDate && (outcome === 'won' || outcome === 'lost');
  // Hoje no fuso do navegador, em AAAA-MM-DD (o input type=date fala esse formato).
  const todayStr = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  useEffect(() => {
    if (isOpen) {
      setReason('');
      setSaleValue('');
      setOutcomeAt(todayStr());
      // Foco no primeiro campo: o operador vem de um arrasto, o teclado tem
      // que estar pronto sem exigir mais um clique.
      const t = setTimeout(() => {
        (showValue ? valueRef.current : inputRef.current)?.focus();
      }, 60);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isOpen, showValue]);

  if (!isOpen) return null;

  const tint = isWon ? colors.accentPrimary : colors.danger;
  const Icon = isWon ? Trophy : XCircle;
  const safeOptions = Array.isArray(options)
    ? options.filter((o) => typeof o === 'string' && o.trim())
    : [];
  // Mesma comparação do backend (minúsculo, sem acento): o atalho fica marcado
  // mesmo se o texto tiver sido digitado com outra caixa/acentuação.
  const norm = (s) => String(s || '')
    .normalize('NFD').replace(DIACRITICS, '')
    .toLowerCase().trim().replace(/\s+/g, ' ');

  // Convenção pt-BR (igual ao backend): ponto é milhar, vírgula é decimal.
  // "112.000" -> 112000; "112000,50" -> 112000.5. Inválido/vazio -> null.
  const parsedValue = () => {
    const s = String(saleValue || '').replace(/[^\d.,]/g, '');
    if (!s) return null;
    const n = parseFloat(s.replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  const validDate = () => {
    const v = String(outcomeAt || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v) || v > todayStr()) return null;
    return v;
  };

  const extra = () => ({
    saleValue: showValue ? parsedValue() : null,
    outcomeAt: showDate ? validDate() : null,
  });

  const skip = () => onSkip(extra());

  const confirm = () => {
    const txt = reason.trim();
    if (txt) onConfirm(txt.slice(0, 500), extra());
    else skip();
  };

  return (
    <div
      onClick={skip}
      style={{
        position: 'fixed', inset: 0, zIndex: 1400,
        backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 440, borderRadius: 14,
          backgroundColor: colors.bgPrimary,
          border: `1px solid ${colors.borderColor}`,
          boxShadow: colors.shadowLg,
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '16px 18px',
          borderBottom: `1px solid ${colors.borderColor}`,
        }}>
          <div style={{
            width: 34, height: 34, borderRadius: 9, flexShrink: 0,
            backgroundColor: `${tint}15`, color: tint,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon size={17} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: colors.textPrimary }}>
              {isWon ? 'Registrar o ganho' : 'Registrar a perda'}
            </h3>
            <p style={{
              margin: '2px 0 0', fontSize: 12, color: colors.textSecondary,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {contactName ? `${contactName} · ` : ''}{labelName}
            </p>
          </div>
          <button
            onClick={skip}
            title="Fechar sem motivo"
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 6,
              color: colors.textSecondary, borderRadius: 8, display: 'flex',
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* Valor da venda: ponto natural de captura do VGV (o time esquecia
              de preencher depois). Enter aqui leva ao motivo, não fecha. */}
          {showValue && (
            <>
              <label style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>
                Valor da venda (R$)
              </label>
              <input
                ref={valueRef}
                value={saleValue}
                inputMode="decimal"
                placeholder="Ex.: 85.000"
                onChange={(e) => setSaleValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); inputRef.current?.focus(); }
                  if (e.key === 'Escape') { e.preventDefault(); skip(); }
                }}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 10,
                  fontSize: 13, border: `1px solid ${colors.inputBorder || colors.borderColor}`,
                  backgroundColor: colors.inputBg || colors.bgSecondary,
                  color: colors.textPrimary, outline: 'none', fontFamily: 'inherit',
                }}
              />
            </>
          )}
          {showDate && (
            <>
              <label style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>
                {isWon ? 'Data da venda' : 'Data da perda'}
              </label>
              <input
                type="date"
                value={outcomeAt}
                max={todayStr()}
                onChange={(e) => setOutcomeAt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); inputRef.current?.focus(); }
                  if (e.key === 'Escape') { e.preventDefault(); skip(); }
                }}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 10,
                  fontSize: 13, border: `1px solid ${colors.inputBorder || colors.borderColor}`,
                  backgroundColor: colors.inputBg || colors.bgSecondary,
                  color: colors.textPrimary, outline: 'none', fontFamily: 'inherit',
                }}
              />
              <span style={{ fontSize: 11, color: colors.textSecondary, marginTop: -2 }}>
                {isWon
                  ? 'O painel conta a venda no mês desta data, não no dia em que o card foi movido.'
                  : 'O painel conta a perda nesta data.'}
              </span>
            </>
          )}
          <label style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>
            Qual foi o motivo?
          </label>

          {/* Atalhos: clicar preenche o campo abaixo. O texto continua sendo o
              ÚNICO valor gravado — a lista só padroniza o que se escreve, e
              digitar um motivo novo faz ele entrar na lista da etapa. */}
          {safeOptions.length > 0 && (
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 6,
              maxHeight: 132, overflowY: 'auto', paddingBottom: 2,
            }}>
              {safeOptions.map((opt) => {
                const selected = norm(reason) === norm(opt);
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setReason(selected ? '' : opt)}
                    style={{
                      padding: '5px 10px', borderRadius: 999, fontSize: 12,
                      cursor: 'pointer', maxWidth: '100%',
                      backgroundColor: selected ? `${tint}15` : colors.bgSecondary,
                      border: `1px solid ${selected ? tint : colors.borderColor}`,
                      color: selected ? tint : colors.textSecondary,
                      fontWeight: selected ? 600 : 400,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          )}
          <textarea
            ref={inputRef}
            value={reason}
            maxLength={500}
            rows={4}
            onChange={(e) => setReason(e.target.value)}
            onKeyDown={(e) => {
              // Enter envia; Shift+Enter quebra linha. Esc pula.
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); confirm(); }
              if (e.key === 'Escape') { e.preventDefault(); skip(); }
            }}
            placeholder={isWon
              ? 'O que fez o cliente fechar?'
              : 'O que fez o cliente desistir?'}
            style={{
              width: '100%', resize: 'vertical', padding: '10px 12px',
              borderRadius: 10, fontSize: 13, lineHeight: 1.45,
              border: `1px solid ${colors.inputBorder || colors.borderColor}`,
              backgroundColor: colors.inputBg || colors.bgSecondary,
              color: colors.textPrimary, outline: 'none', fontFamily: 'inherit',
            }}
          />
          <span style={{ fontSize: 11, color: colors.textSecondary }}>
            Opcional. O contato é movido de qualquer forma.
            {showValue && ' O valor é salvo mesmo sem motivo.'}
            {showDate && ' A data é salva mesmo sem motivo.'}
            {safeOptions.length > 0 && ' Um motivo novo entra na lista da etapa.'}
          </span>
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          padding: '12px 18px 16px',
        }}>
          <button
            onClick={skip}
            style={{
              padding: '9px 16px', borderRadius: 9, fontSize: 13, fontWeight: 600,
              cursor: 'pointer', backgroundColor: 'transparent',
              border: `1px solid ${colors.borderColor}`, color: colors.textSecondary,
            }}
          >
            Pular
          </button>
          {(() => {
            // Valor digitado sem motivo também habilita o botão: confirm() cai
            // no skip(), que salva o valor e move do mesmo jeito.
            const canSave = !!reason.trim() || (showValue && !!saleValue.trim()) || showDate;
            return (
              <button
                onClick={confirm}
                disabled={!canSave}
                style={{
                  padding: '9px 18px', borderRadius: 9, fontSize: 13, fontWeight: 600,
                  cursor: canSave ? 'pointer' : 'not-allowed',
                  backgroundColor: canSave ? tint : colors.bgSecondary,
                  border: `1px solid ${canSave ? tint : colors.borderColor}`,
                  color: canSave ? '#fff' : colors.textSecondary,
                }}
              >
                Salvar
              </button>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default OutcomeReasonModal;
